import type {
  Provenance,
  Evidence,
  EvidenceItem,
  DataSource,
  CreateEvidenceOptions,
  CreateIngestProvenanceOptions,
  ProvenanceConflict
} from '../types/provenance'
import { detectPropertyConflicts } from './conflict'

const DEFAULT_EXTRACTION_MODEL = 'unknown-model'

function now(): string {
  return new Date().toISOString()
}

function clampConfidence(confidence: number): number {
  if (!Number.isFinite(confidence)) return 0
  if (confidence < 0) return 0
  if (confidence > 1) return 1
  return confidence
}

function defaultExtractionMethod(source: DataSource): string {
  if (source === 'deterministic') return 'deterministic'
  if (source === 'manual') return 'manual-entry'
  return 'text-structured'
}

function toEvidenceItem(evidence: Evidence): EvidenceItem {
  return {
    source_file: evidence.source_file ?? 'unknown-source',
    source_page: evidence.source_page,
    source_excerpt: evidence.source_excerpt,
    confidence: clampConfidence(evidence.confidence),
    extraction_model: evidence.model_version ?? DEFAULT_EXTRACTION_MODEL,
    extracted_at: evidence.created_at
  }
}

export function evidenceItemKey(item: EvidenceItem): string {
  return [
    item.source_file,
    item.source_page ?? '',
    item.source_excerpt ?? '',
    item.extraction_model
  ].join('|')
}

/** Dedup/merge evidence_items by source identity; sorted by extracted_at. */
export function mergeEvidenceItems(
  existing: EvidenceItem[],
  incoming: EvidenceItem[]
): EvidenceItem[] {
  const byKey = new Map<string, EvidenceItem>()
  for (const item of existing) {
    byKey.set(evidenceItemKey(item), item)
  }

  for (const item of incoming) {
    byKey.set(evidenceItemKey(item), item)
  }

  return [...byKey.values()].sort((a, b) => a.extracted_at.localeCompare(b.extracted_at))
}

function aggregateConfidence(existing: number | undefined, incoming: number): number {
  if (existing === undefined) return clampConfidence(incoming)
  return Math.max(clampConfidence(existing), clampConfidence(incoming))
}

function latestIsoTimestamp(a: string | undefined, b: string): string {
  if (!a) return b
  return a > b ? a : b
}

/**
 * Build a deterministic evidence ID from source information the caller already has.
 * Same inputs always produce the same ID, enabling idempotent writes.
 */
/**
 * Delimiter for evidence IDs. Uses ASCII Record Separator (\x1E) to avoid
 * collisions with any characters in source_kind, file paths, or URNs.
 */
export const EVIDENCE_ID_DELIM = '\x1E'

export function evidenceId(parts: {
  source_kind: string
  source_file?: string
  path?: string
}): string {
  return [parts.source_kind, parts.source_file, parts.path].filter(Boolean).join(EVIDENCE_ID_DELIM)
}

export class ProvenanceWriter {
  createEvidence(id: string, opts: CreateEvidenceOptions): Evidence {
    const createdAt = opts.extracted_at ?? now()
    return {
      id,
      source: opts.source,
      source_kind: opts.source_kind,
      confidence: clampConfidence(opts.confidence),
      source_file: opts.source_file ?? 'unknown-source',
      created_by: opts.created_by,
      created_at: createdAt,
      model_version: opts.model_version ?? opts.extraction_model ?? DEFAULT_EXTRACTION_MODEL,
      source_page: opts.source_page,
      source_region: opts.source_region,
      source_excerpt: opts.source_excerpt,
      extraction_method: opts.extraction_method ?? defaultExtractionMethod(opts.source)
    }
  }

  emptyProvenance(): Provenance {
    return { evidence: {}, evidence_items: [] }
  }

  createIngestProvenance(opts: CreateIngestProvenanceOptions): Provenance {
    const extractedAt = opts.extracted_at ?? now()
    const id = evidenceId({
      source_kind: opts.source_kind,
      source_file: opts.source_file,
      path: `${opts.source_page ?? ''}:${opts.source_region ?? ''}:${opts.source_excerpt ?? ''}`
    })

    const evidence = this.createEvidence(id, {
      source:
        opts.source_kind === 'manual'
          ? 'manual'
          : opts.source_kind === 'deterministic'
            ? 'deterministic'
            : 'semantic',
      source_kind: opts.source_kind,
      source_file: opts.source_file,
      source_page: opts.source_page,
      source_region: opts.source_region,
      source_excerpt: opts.source_excerpt,
      confidence: opts.confidence,
      extraction_method: opts.extraction_method,
      extraction_model: opts.extraction_model,
      extracted_at: extractedAt
    })

    const withEvidence = this.addEvidence(this.emptyProvenance(), evidence)
    return {
      ...withEvidence,
      source_file: opts.source_file,
      source_kind: opts.source_kind,
      source_page: opts.source_page,
      source_region: opts.source_region,
      source_excerpt: opts.source_excerpt,
      extraction_model: opts.extraction_model,
      extraction_method: opts.extraction_method,
      confidence: clampConfidence(opts.confidence),
      last_evidence_at: extractedAt
    }
  }

  /**
   * Build an evidence ID, create evidence, and wrap it in a fresh Provenance — all in one call.
   */
  createProvenance(
    path: string,
    sourceKind: string,
    source: DataSource,
    confidence: number,
    sourceFile?: string
  ): Provenance {
    const id = evidenceId({ source_kind: sourceKind, source_file: sourceFile, path })
    const evidence = this.createEvidence(id, {
      source,
      source_kind: sourceKind,
      confidence,
      source_file: sourceFile
    })
    return this.addEvidence(this.emptyProvenance(), evidence)
  }

  addEvidence(provenance: Provenance, evidence: Evidence): Provenance {
    const evidenceItems = mergeEvidenceItems(provenance.evidence_items ?? [], [
      toEvidenceItem(evidence)
    ])

    return {
      ...provenance,
      evidence: { ...provenance.evidence, [evidence.id]: evidence },
      evidence_items: evidenceItems,
      source_file: provenance.source_file ?? evidence.source_file,
      source_kind: provenance.source_kind ?? evidence.source_kind,
      source_page: provenance.source_page ?? evidence.source_page,
      source_region: provenance.source_region ?? evidence.source_region,
      source_excerpt: provenance.source_excerpt ?? evidence.source_excerpt,
      extraction_model:
        provenance.extraction_model ?? evidence.model_version ?? DEFAULT_EXTRACTION_MODEL,
      extraction_method:
        provenance.extraction_method ??
        evidence.extraction_method ??
        defaultExtractionMethod(evidence.source),
      confidence: aggregateConfidence(provenance.confidence, evidence.confidence),
      last_evidence_at: latestIsoTimestamp(provenance.last_evidence_at, evidence.created_at)
    }
  }

  removeEvidence(provenance: Provenance, evidenceId: string): Provenance {
    const removed = provenance.evidence[evidenceId]
    const { [evidenceId]: _, ...rest } = provenance.evidence
    const filteredEvidenceItems = removed
      ? (provenance.evidence_items ?? []).filter(
          (item) => evidenceItemKey(item) !== evidenceItemKey(toEvidenceItem(removed))
        )
      : (provenance.evidence_items ?? [])

    const latest = filteredEvidenceItems[filteredEvidenceItems.length - 1]
    const maxConfidence = filteredEvidenceItems.reduce(
      (acc, item) => Math.max(acc, item.confidence),
      0
    )

    return {
      ...provenance,
      evidence: rest,
      evidence_items: filteredEvidenceItems,
      confidence: filteredEvidenceItems.length > 0 ? maxConfidence : undefined,
      last_evidence_at: latest?.extracted_at
    }
  }

  /**
   * Merge incoming evidence into existing provenance.
   * Same ID = update in place. New ID = append.
   */
  mergeEvidence(
    existing: Provenance,
    incoming: Evidence[]
  ): { provenance: Provenance; added: number; updated: number } {
    let mergedProvenance: Provenance = { ...existing }
    let added = 0
    let updated = 0

    for (const item of incoming) {
      if (item.id in (mergedProvenance.evidence ?? {})) {
        updated++
      } else {
        added++
      }
      mergedProvenance = this.addEvidence(mergedProvenance, item)
    }

    return { provenance: mergedProvenance, added, updated }
  }

  /**
   * Apply incoming properties to existing, respecting source priority.
   * Returns the merged properties and any conflicts detected.
   *
   * Not used by the agent gateway accumulate path: same-source priority
   * resolves to accept_incoming (last-write-wins for semantic↔semantic).
   * That path uses detectPropertyConflicts for detection only and uniformly
   * keeps the existing value + records the conflict.
   */
  applyUpdate(
    existingProps: Record<string, unknown>,
    existingSource: DataSource,
    incomingProps: Record<string, unknown>,
    incomingSource: DataSource,
    nodeContext?: { node_id?: string; node_label?: string }
  ): { merged: Record<string, unknown>; conflicts: ProvenanceConflict[] } {
    const conflicts = detectPropertyConflicts(
      existingProps,
      existingSource,
      incomingProps,
      incomingSource,
      nodeContext
    )

    const merged = { ...existingProps }

    for (const [key, value] of Object.entries(incomingProps)) {
      if (!(key in existingProps)) {
        merged[key] = value
        continue
      }

      if (existingProps[key] === value) continue

      const conflict = conflicts.find((c) => c.property === key)
      if (!conflict || conflict.suggested_resolution === 'accept_incoming') {
        merged[key] = value
      }
    }

    return { merged, conflicts }
  }
}

// -- Provenance query utilities --

export function highestPrioritySource(provenance: Provenance): DataSource | undefined {
  const priorities: Record<DataSource, number> = {
    deterministic: 3,
    manual: 2,
    semantic: 1
  }
  let best: DataSource | undefined
  let bestPriority = 0

  for (const item of Object.values(provenance.evidence ?? {})) {
    const p = priorities[item.source]
    if (p > bestPriority) {
      bestPriority = p
      best = item.source
    }
  }

  return best
}

export function latestEvidence(provenance: Provenance): Evidence | undefined {
  let latest: Evidence | undefined
  for (const item of Object.values(provenance.evidence ?? {})) {
    if (!latest || item.created_at > latest.created_at) {
      latest = item
    }
  }
  return latest
}

/**
 * Latest `manual` evidence entry — a human correction/annotation. The read-side
 * surfaces its `source_excerpt` as the agent-facing note for a node, so callers
 * can answer "why does the agent treat this node this way?" from provenance.
 */
export function latestManualEvidence(provenance: Provenance): Evidence | undefined {
  let latest: Evidence | undefined
  for (const item of Object.values(provenance.evidence ?? {})) {
    if (item.source !== 'manual') continue
    if (!latest || item.created_at > latest.created_at) {
      latest = item
    }
  }
  return latest
}

export function confidenceRange(provenance: Provenance): { min: number; max: number } | undefined {
  const items = Object.values(provenance.evidence ?? {})
  if (items.length === 0) return undefined

  let min = Infinity
  let max = -Infinity
  for (const item of items) {
    if (item.confidence < min) min = item.confidence
    if (item.confidence > max) max = item.confidence
  }

  return { min, max }
}
